import { z } from "zod";

export enum RuleBasedAudienceCombinatorOperator {
  INTERSECT = "intersect",
  UNION = "union",
  DIFF = "diff",
}

export enum RuleBasedAudienceCondition {
  CONTAINS_ANY_OF = "contains_any_of",
  CONTAINS_NONE_OF = "contains_none_of",
  CONTAINS_ALL_OF = "contains_all_of",
  EMPTY = "empty",
  NOT_EMPTY = "not_empty",
}

export enum RuleBasedAudienceFiltersBooleanOperator {
  AND = "and",
  OR = "or",
}

const RuleBasedAudienceFilterSchema = z.object({
  attribute: z.string().trim().min(1),
  condition: z.nativeEnum(RuleBasedAudienceCondition),
  values: z.array(z.string()).nullable(),
});

const RuleBasedAudienceFilterSchemaLoose = RuleBasedAudienceFilterSchema.extend(
  {
    attribute: RuleBasedAudienceFilterSchema.shape.attribute.nullable(),
    condition: RuleBasedAudienceFilterSchema.shape.condition.nullable(),
  }
);

export type RuleBasedAudienceFilter = z.infer<
  typeof RuleBasedAudienceFilterSchemaLoose
>;

const RuleBasedAudienceFiltersSchema = z.object({
  filters: z.array(
    RuleBasedAudienceFilterSchema.refine(
      (data) =>
        ([
          RuleBasedAudienceCondition.EMPTY,
          RuleBasedAudienceCondition.NOT_EMPTY,
        ].includes(data.condition) &&
          !data.values?.length) ||
        (![
          RuleBasedAudienceCondition.EMPTY,
          RuleBasedAudienceCondition.NOT_EMPTY,
        ].includes(data.condition) &&
          data.values?.length),
      {
        message: "Values must be empty for conditions 'empty' and 'not_empty'",
        path: ["values"],
      }
    )
  ),
  operator: z.nativeEnum(RuleBasedAudienceFiltersBooleanOperator),
});

const RuleBasedAudienceFiltersSchemaLoose =
  RuleBasedAudienceFiltersSchema.extend({
    filters: RuleBasedAudienceFilterSchemaLoose.array(),
  });

export type RuleBasedAudienceFilters = z.infer<
  typeof RuleBasedAudienceFiltersSchemaLoose
>;

const RuleBasedAudienceCombineSchema = z.object({
  filters: z.optional(z.nullable(RuleBasedAudienceFiltersSchema)),
  name: z.string().trim().min(1),
  operator: z.nativeEnum(RuleBasedAudienceCombinatorOperator),
  sourceRef: z.string().trim().min(1),
});

export type RuleBasedAudienceCombine = z.infer<
  typeof RuleBasedAudienceCombineSchema
>;

const RuleBasedAudienceCombineSchemaLoose =
  RuleBasedAudienceCombineSchema.extend({
    filters: z.optional(z.nullable(RuleBasedAudienceFiltersSchemaLoose)),
    sourceRef: z.string().trim().min(1).nullable(),
  });

export const RuleBasedAudienceSchema = z.object({
  combine: z.optional(z.nullable(z.array(RuleBasedAudienceCombineSchema))),
  filters: z.optional(z.nullable(RuleBasedAudienceFiltersSchema)),
  id: z.string().trim().min(1),
  name: z.string().trim().min(1),
  sourceRef: z.string().trim().min(1),
});

const RuleBasedAudienceSchemaLoose = RuleBasedAudienceSchema.extend({
  combine: z.optional(z.nullable(z.array(RuleBasedAudienceCombineSchemaLoose))),
  filters: z.optional(z.nullable(RuleBasedAudienceFiltersSchemaLoose)),
  sourceRef: z.string().trim().min(1).nullable(),
}).omit({ id: true });

export type DraftRuleBasedAudience = z.infer<
  typeof RuleBasedAudienceSchemaLoose
>;

export type AudienceRulesTree = z.infer<typeof RuleBasedAudienceSchema>;

export enum AudienceAttributeValueType {
  NUMERIC_RANGE = "NUMERIC_RANGE",
  CATEGORICAL = "CATEGORICAL",
}

export interface AudienceAttributes {
  [key: string]: {
    values: string[];
    filters: RuleBasedAudienceCondition[];
    valueType: AudienceAttributeValueType;
  };
}

export type AudienceSeedDataGroup =
  | "Base"
  | "Seed"
  | "Remarketing"
  | "Lookalike"
  | "Rule-based";

export interface AudienceSeedOption {
  attributes: AudienceAttributes;
  id: string;
  name: string;
  group: AudienceSeedDataGroup;
}
